// src/pages/reports/ReportsArchive.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Spinner,
  IconButton,
  Tooltip,
  useToast,
  Link,
  Select as CSelect,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import useOrgClaims from '../../hooks/useOrgClaims';

/* --- helpers --- */
const fmtDateShort = (ts) => {
  try {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  } catch {
    return '—';
  }
};
const fmtTime24 = (ts) => {
  try {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mi}`;
  } catch {
    return '—';
  }
};
const fmtUpdatedShort = (ts) => (ts ? `${fmtDateShort(ts)} ${fmtTime24(ts)}` : '—');

const DNC_REASONS = [
  'Do Not Contact',
  'Not Interested',
  'Wrong Number',
  'Abusive/Threatening',
  'Spam/Scam',
  'Duplicate Lead',
  'Other',
];

/** Pull the previous status from a lead.statusHistory entry
 * that moved the lead into ARCHIVE_DNC.
 */
function getPreviousStatusFromHistory(lead) {
  const hist = Array.isArray(lead?.statusHistory) ? lead.statusHistory : [];
  for (let i = hist.length - 1; i >= 0; i--) {
    const h = hist[i] || {};
    if ((h.to || '').toUpperCase() === 'ARCHIVE_DNC') {
      const prev = (h.from || '').toUpperCase();
      if (prev) return prev;
      break;
    }
  }
  return null;
}

export default function ReportsArchive() {
  const { orgId, user } = useOrgClaims();
  const toast = useToast();

  // list state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  // filters
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('ALL');
  const [prevStatusFilter, setPrevStatusFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(''); // YYYY-MM-DD

  // For the Previous Status filter dropdown (built dynamically from data)
  const prevStatusSet = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.prevStatus) set.add(r.prevStatus);
    });
    return Array.from(set).sort();
  }, [rows]);

  // ---------- SERVER-SIDE FILTER (status == ARCHIVE_DNC) ----------
  const fetchBatch = useCallback(
    async (afterDoc = null) => {
      if (!orgId) return { items: [], lastDoc: null };

      const baseQ = query(
        collection(db, 'organizations', orgId, 'leads'),
        where('status', '==', 'ARCHIVE_DNC'),
        orderBy('createdAt', 'desc'),
        limit(25)
      );
      const q2 = afterDoc ? query(baseQ, startAfter(afterDoc)) : baseQ;

      const snap = await getDocs(q2);
      const items = [];
      snap.forEach((d) => {
        const x = { id: d.id, ...(d.data() || {}) };
        const parts = `${x.customerFirstName || ''} ${x.customerLastName || ''}`.trim();
        const baseName = x.customerFullName || parts || '—';
        const titledName = x.customerTitle ? `${x.customerTitle} ${baseName}` : baseName;

        const prevStatus =
          getPreviousStatusFromHistory(x) ||
          (x.dncPrevStatus ? String(x.dncPrevStatus).toUpperCase() : null);

        items.push({
          leadId: d.id,
          customer: titledName,
          email: x.customerEmail || '—',
          phone: x.customerPhone || '—',
          postcode: x.postcode || x.addressPostcode || '—',
          updatedAt: x.updatedAt || null,
          lastActivityAt: x.lastActivityAt || x.updatedAt || null,
          reason: x.dncReason || 'Do Not Contact',
          prevStatus: prevStatus || '—',
          _raw: x,
        });
      });

      return { items, lastDoc: snap.docs[snap.docs.length - 1] || null };
    },
    [orgId]
  );

  useEffect(() => {
    if (!orgId) {
      setRows([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { items, lastDoc } = await fetchBatch(null);
        setRows(items);
        setCursor(lastDoc);
      } catch (e) {
        toast({ title: 'Failed to load', description: e.message || String(e), status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, fetchBatch, toast]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const { items, lastDoc } = await fetchBatch(cursor);
      setRows((prev) => prev.concat(items));
      setCursor(lastDoc);
    } catch (e) {
      toast({ title: 'Failed to load more', description: e.message || String(e), status: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }

  // Filter logic (date by lastActivityAt)
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;

    return rows.filter((r) => {
      const matchesSearch =
        !term ||
        [r.customer, r.email, r.phone, r.postcode].some((f) =>
          (f || '').toLowerCase().includes(term)
        );

      const matchesReason =
        reasonFilter === 'ALL' || (r.reason || '').toLowerCase() === reasonFilter.toLowerCase();

      const matchesPrev = prevStatusFilter === 'ALL' || (r.prevStatus || '') === prevStatusFilter;

      let matchesDate = true;
      if (from || to) {
        const dt = r.lastActivityAt
          ? typeof r.lastActivityAt.toDate === 'function'
            ? r.lastActivityAt.toDate()
            : new Date(r.lastActivityAt)
          : null;
        if (!dt) {
          matchesDate = false;
        } else {
          if (from && dt < from) matchesDate = false;
          if (to && dt > to) matchesDate = false;
        }
      }

      return matchesSearch && matchesReason && matchesPrev && matchesDate;
    });
  }, [rows, search, reasonFilter, prevStatusFilter, dateFrom, dateTo]);

  // Restore: back to previous status (from statusHistory). If none, fall back to PMDNH (so it shows in Opportunities).
  async function handleRestore(row) {
    const leadId = row.leadId;
    try {
      const leadRef = doc(db, 'organizations', orgId, 'leads', leadId);
      const snap = await getDoc(leadRef);
      const lead = snap.data() || {};
      let targetStatus = getPreviousStatusFromHistory(lead);
      if (!targetStatus) targetStatus = 'PMDNH';

      const hist = Array.isArray(lead.statusHistory) ? [...lead.statusHistory] : [];
      hist.push({
        at: new Date().toISOString(),
        byUid: user?.uid || null,
        from: 'ARCHIVE_DNC',
        to: targetStatus,
      });

      await updateDoc(leadRef, {
        status: targetStatus,
        doNotContact: false,
        dncReason: null,
        dncNote: null,
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        statusHistory: hist,
      });

      setRows((prev) => prev.filter((r) => r.leadId !== leadId));
      toast({
        title: 'Lead restored',
        description: `Status set to ${targetStatus}`,
        status: 'success',
      });
    } catch (e) {
      toast({ title: 'Restore failed', description: e?.message || String(e), status: 'error' });
    }
  }

  return (
    <Box>
      <Heading size="lg" color="blue.600" mb={2}>
        Archive (Do Not Contact)
      </Heading>
      <Text color="gray.600" mb={4}>
        Leads/Contacts marked as <b>ARCHIVE_DNC</b>. DNC: Do Not Contact.
        They are excluded from KPIs and active reports.
      </Text>

      {/* Filters */}
      <VStack align="stretch" spacing={3} mb={4}>
        <HStack spacing={3}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer / email / phone / postcode"
            maxW="420px"
          />
          <CSelect
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            maxW="240px"
          >
            <option value="ALL">All reasons</option>
            {DNC_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </CSelect>
          <CSelect
            value={prevStatusFilter}
            onChange={(e) => setPrevStatusFilter(e.target.value)}
            maxW="220px"
          >
            <option value="ALL">All previous statuses</option>
            {prevStatusSet.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </CSelect>
        </HStack>
        <HStack spacing={3}>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              From (by last activity)
            </Text>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              maxW="180px"
            />
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              To (by last activity)
            </Text>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              maxW="180px"
            />
          </Box>
        </HStack>
      </VStack>

      {loading ? (
        <HStack>
          <Spinner />
          <Text>Loading…</Text>
        </HStack>
      ) : (
        <>
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Reason</Th>
                <Th>Previous Status</Th>
                <Th>Email</Th>
                <Th>Tel</Th>
                <Th>Postcode</Th>
                <Th>Updated</Th>
                <Th textAlign="right" w="150px">
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((r) => (
                <Tr key={r.leadId}>
                  <Td>
                    <HStack spacing={2}>
                      <Text>{r.customer}</Text>
                      <Link
                        as={RouterLink}
                        to={`/leads/${r.leadId}/edit`}
                        title="Open lead"
                        color="teal.600"
                      >
                        <ExternalLinkIcon />
                      </Link>
                    </HStack>
                  </Td>
                  <Td>{r.reason}</Td>
                  <Td>
                    <Tag>{r.prevStatus}</Tag>
                  </Td>
                  <Td>{r.email}</Td>
                  <Td>{r.phone}</Td>
                  <Td>{r.postcode}</Td>
                  <Td>{fmtUpdatedShort(r.updatedAt)}</Td>
                  <Td textAlign="right" whiteSpace="nowrap">
                    <HStack spacing={2} justify="flex-end" minW="140px">
                      <Tooltip label="Restore">
                        <IconButton
                          size="xs"
                          icon={<RepeatIcon />}
                          aria-label="Restore"
                          onClick={() => handleRestore(r)}
                        />
                      </Tooltip>
                      <Tooltip label="Lead">
                        <Link as={RouterLink} to={`/leads/${r.leadId}/edit`} title="Open lead">
                          <IconButton size="xs" icon={<ExternalLinkIcon />} aria-label="Open" />
                        </Link>
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {filtered.length === 0 && (
                <Tr>
                  <Td colSpan={8}>
                    <Text color="gray.600">No archived leads.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          <HStack mt={4}>
            <Button onClick={loadMore} isDisabled={!cursor} isLoading={loadingMore}>
              Load more
            </Button>
            {!cursor && (
              <Text color="gray.500" fontSize="sm">
                End of list
              </Text>
            )}
          </HStack>
        </>
      )}
    </Box>
  );
}
